import { useCallback } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation } from '@apollo/client';
import { usePostHog } from 'posthog-js/react';

import type { PreOnboardingParams, SetupForm } from '../pages/PreOnboarding/PreOnboardingPage';

import { ENROLL_DIRECT_ACCESS_PATIENT_V2 } from '@/apollo/mutations';
import { QUERY_DISTINCT_ID_WITH_EMAIL } from '@/apollo/queries';
import { EN } from '@/utils/constants';
import { getPdtUid } from '@/utils/productHelpers';
import { getProgram } from '@/utils/posthog';
import { useUserUid } from '@/hooks/useUserUid';
import { useUtmParams } from '@/hooks/useUtmParams';
import { usePostHogDistinctId } from '@/hooks/usePostHogDistinctId';
import { parseSearchParams } from '@/utils/stringUtils';

type EnrollArgs = Pick<SetupForm, 'email' | 'phoneNumber' | 'marketingConsent'>;

export const useEnrollDirectAccessPatient = () => {
  const posthog = usePostHog();
  const { i18n } = useTranslation();
  const location = useLocation();
  const { utmParams } = useUtmParams();
  const { userUid, setUserUid } = useUserUid();
  const { product, slug } = useParams<PreOnboardingParams>();

  const searchParams = parseSearchParams(location.search);
  const {
    distinctId: urlDistinctId,
    loading: distinctIdLoading,
  } = usePostHogDistinctId();

  const [enrollDirectAccessPatientV2, { loading: enrollLoading }] = useMutation(ENROLL_DIRECT_ACCESS_PATIENT_V2);

  const [getDistinctId] = useLazyQuery(QUERY_DISTINCT_ID_WITH_EMAIL, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const getPostHogDistinctId = useCallback(async (email: string): Promise<string> => {
    try {
      const result = await getDistinctId({
        variables: { email },
        onCompleted: (data) => {
          const distinctId = data?.posthogDistinctIdWithEmail?.distinctId;
          if (distinctId) {
            posthog?.identify(distinctId, { email });
          }
        },
      });

      return result?.data?.posthogDistinctIdWithEmail?.distinctId ?? '';
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching PostHog distinct ID:', error);
      return '';
    }
  }, [getDistinctId, posthog]);

  const enroll = useCallback(async (data: EnrollArgs) => {
    const trimmedEmail = data.email.trim();

    // Use distinct ID from URL if available, otherwise fetch from Cortex
    const posthogDistinctId = urlDistinctId || await getPostHogDistinctId(trimmedEmail);

    const response = await enrollDirectAccessPatientV2({
      variables: {
        email: trimmedEmail,
        language: i18n.language.toUpperCase().split('-')[0] || EN,
        organizationSlug: slug,
        pdtUid: getPdtUid(product),
        termsAndPrivacyAcknowledgedAt: new Date().toISOString(),
        posthogDistinctId,
        phone: data.phoneNumber || null,
        isDependent: searchParams.autoSelect === 'dependent',
        marketingSmsConsent: data.marketingConsent || false,
        ...utmParams,
      },
    });

    const result = response.data?.enrollDirectAccessPatientV2;

    if (result?.success) {
      const cortexId = result.userUid || result.patientUid;
      setUserUid(cortexId);

      posthog.capture('ActivationFlowSubmitted', {
        email: trimmedEmail,
        program: getProgram(product),
        'wellness.cortex_id': cortexId,
      });

      return { success: true, cortexId };
    }

    posthog.capture('ActivationFlowErrored', {
      email: trimmedEmail,
      program: getProgram(product),
    });

    return { success: false };
  }, [
    enrollDirectAccessPatientV2,
    getPostHogDistinctId,
    i18n,
    posthog,
    product,
    searchParams.autoSelect,
    setUserUid,
    slug,
    urlDistinctId,
    utmParams,
  ]);

  const loading = distinctIdLoading || enrollLoading;

  return {
    enroll, loading, userUid,
  };
};
